import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  sectorAngle?: number;
  highlightSector?: boolean;
  mode?: 'stimulus' | 'support';
}
/**
 * Helper: build an SVG arc path for a sector from 0° to `angleDeg`.
 * The sector starts at 12-o'clock (−90°) and sweeps clockwise.
 */
function sectorPath(cx: number, cy: number, r: number, angleDeg: number): string {
  if (angleDeg >= 360) {
    // Full circle — use two arcs
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const startRad = -Math.PI / 2; // 12 o'clock
  const endRad = startRad + angleDeg * Math.PI / 180;
  const largeArc = angleDeg > 180 ? 1 : 0;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  sectorAngle,
  highlightSector = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const maxExpectedRadius = 20;
  const scaleFactor = maxDisplayRadius / maxExpectedRadius;
  const circleRadius = Math.max(minDisplayRadius, Math.min(maxDisplayRadius, radius * scaleFactor));
  const hasSector = typeof sectorAngle === 'number' && sectorAngle > 0 && sectorAngle < 360;
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-12661" data-mp="1hd8hhoeev4gv">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-12662" data-mp="1m4gqijbtwnti">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" role="img" aria-label={hasSector ? `Cirkel met sector van ${sectorAngle}°` : 'Cirkel'} data-id="element-12663" data-mp="1r2i9z5d62p5i">
        {/* Full circle background */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill={hasSector ? 'rgb(241, 245, 249)' : 'rgb(191, 219, 254)'} stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-12664" data-mp="2esmcf9emciux" />

        {/* Sector wedge (when sectorAngle is provided) */}
        {hasSector && <motion.path d={sectorPath(cx, cy, circleRadius, sectorAngle)} fill="rgb(147, 197, 253)" stroke="rgb(59, 130, 246)" strokeWidth="2.5" strokeLinejoin="round" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5,
        ease: 'easeOut'
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-12665" data-mp="3ljs44n6wgmxd" />}

        {/* Sector angle arc indicator */}
        {hasSector && sectorAngle <= 180 && (() => {
        const arcR = circleRadius * 0.25;
        const startRad = -Math.PI / 2;
        const endRad = startRad + sectorAngle * Math.PI / 180;
        const largeArc = sectorAngle > 180 ? 1 : 0;
        const ax1 = cx + arcR * Math.cos(startRad);
        const ay1 = cy + arcR * Math.sin(startRad);
        const ax2 = cx + arcR * Math.cos(endRad);
        const ay2 = cy + arcR * Math.sin(endRad);
        return <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 ${largeArc} 1 ${ax2} ${ay2}`} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="2" strokeDasharray="4,3" data-id="element-12666" data-mp="k7exkp4f86wh" />;
      })()}

        {/* Sector angle label */}
        {hasSector && (() => {
        const labelR = circleRadius * 0.35;
        const midAngleRad = -Math.PI / 2 + sectorAngle * Math.PI / 360;
        const lx = cx + labelR * Math.cos(midAngleRad);
        const ly = cy + labelR * Math.sin(midAngleRad);
        return <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" className="text-sm font-bold fill-indigo-700" data-id="element-12667" data-mp="5liwn0yz5cx5">
                {sectorAngle}°
              </text>;
      })()}

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-12668" data-mp="ajaqi0j6j93z" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-12669" data-mp="k93yyso0z840" />
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-12670" data-mp="3r197mxyuqk34">
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-12671" data-mp="ekvwpnbc0j3m" />
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-12672" data-mp="2hv8wwsddrrg3">
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-4 max-w-md w-full" data-id="element-12673" data-mp="237flljvbvb3b">
          {circumference !== undefined && <div className="flex flex-col gap-1" data-id="element-12674" data-mp="2vuryf2bos4jf">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-12675" data-mp="2vvbeedhs0iut">
                Omtrek (C)
              </span>
              <div className="flex justify-between items-center" data-id="element-12676" data-mp="2mud7nnfydx8j">
                <span className="text-sm text-blue-800" data-id="element-12677" data-mp="2mw6g98a4mrj6">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-12678" data-mp="1fhtmsxivaxvu">
                  C = 2πr
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-12679" data-mp="3ajew17rrv57d">
                <span className="text-sm text-blue-800" data-id="element-12680" data-mp="3ahvi3ntl968r">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-12681" data-mp="3fjkr6xx939u7">
                  2 × π × {radius}
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-12682" data-mp="31kte9eu4ju7n">
                <span className="text-sm font-bold text-blue-900" data-id="element-12683" data-mp="36jxrqho30e3s">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-12684" data-mp="23lo3hbib21x">
                  ≈ {circumference.toFixed(1)} cm
                </span>
              </div>
            </div>}
          {area !== undefined && <div className="flex flex-col gap-1" data-id="element-12685" data-mp="3pfw5ftgei9ra">
              <span className="text-sm font-semibold text-blue-900 border-b border-blue-200 pb-1 mb-1" data-id="element-12686" data-mp="3pel5y5tzno5z">
                Oppervlakte (A)
              </span>
              <div className="flex justify-between items-center" data-id="element-12687" data-mp="lsnqj2b7wfob">
                <span className="text-sm text-blue-800" data-id="element-12688" data-mp="v6644fmwb7ve">Formule:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-12689" data-mp="v4bfaq8i6vm1">
                  A = πr²
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-12690" data-mp="14wqwhlbz7g5v">
                <span className="text-sm text-blue-800" data-id="element-12691" data-mp="14y227qozexzm">Invullen:</span>
                <span className="font-mono font-bold text-blue-700" data-id="element-12692" data-mp="zxo2qx1g4wd7">
                  π × {radius}²
                </span>
              </div>
              <div className="flex justify-between items-center bg-blue-100 p-2 rounded-md mt-1" data-id="element-12693" data-mp="85yjiwkca48b">
                <span className="text-sm font-bold text-blue-900" data-id="element-12694" data-mp="m5pxwyerpmmu">
                  Uitkomst:
                </span>
                <span className="font-mono font-black text-blue-800" data-id="element-12695" data-mp="h63l2q2uq8ek">
                  ≈ {area.toFixed(1)} cm²
                </span>
              </div>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-12696" data-mp="1sl0dm9y8vfqo">
              <p className="text-sm text-blue-900" data-id="element-12697" data-mp="1so62pp6vihme">
                <span className="font-bold" data-id="element-12698" data-mp="1o8xdnd3ip5um">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-12699" data-mp="1jbx42s7glkyr">
                <span className="font-bold" data-id="element-12700" data-mp="1jd89sxkgt2si">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Sector info card (when sector is shown) */}
      {hasSector && mode === 'support' && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-12701" data-mp="15jqzm3do52f5">
          <div className="flex flex-col gap-1" data-id="element-12702" data-mp="10jl6sjvnx9vh">
            <div className="flex items-center gap-2" data-id="element-12703" data-mp="vlbau724658t">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-12704" data-mp="vltcxkbgjdon">
                Sector:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-12705" data-mp="qo1d844kmn9w">
                {sectorAngle}°
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-12706" data-mp="2l0hf6sqc3f8y">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-12707" data-mp="2l10v63wfbtkc">
                Deel:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-12708" data-mp="2tf5i5cn2i4fg">
                {sectorAngle}/360
              </span>
            </div>
          </div>
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && !hasSector && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-12709" data-mp="377v5pli8vheo">
          <div className="flex flex-col gap-1" data-id="element-12710" data-mp="2akqlhto8anus">
            {propRadius && <div className="flex items-center gap-2" data-id="element-12711" data-mp="2ek92iecxiesc">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-12712" data-mp="2jisqslezw2nv">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-12713" data-mp="2ojqa51bqr2qy">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-12714" data-mp="3qmrrcqn11p8">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-12715" data-mp="3a8wpvqbaqwnq">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-12716" data-mp="35avabfd2xfh0">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}